import { ScatterplotLayer } from '@deck.gl/layers';
import type { LayerState, LayerFeature, FlareProps } from '@vitals/shared';
import { LAYER_311 } from '@vitals/shared';
import type { RendererDef } from './registry.js';

// Amber/orange flare color: [r, g, b]
const FLARE_BASE_COLOR: [number, number, number] = [255, 160, 40];

/** Max age (in seconds) at which a flare fully fades out. 72h = 259200s. */
const MAX_AGE_SEC = 72 * 60 * 60;

/**
 * Maps ageSec → opacity [0, 255].
 * Newer complaints (small ageSec) are bright; older ones fade toward dim.
 * Uses an exponential decay so very recent flares pop and older ones trail off.
 */
function ageToOpacity(ageSec: number): number {
  const t = Math.min(ageSec / MAX_AGE_SEC, 1);
  // Exponential: opacity starts near 220 and fades to ~20 at MAX_AGE_SEC.
  const opacity = 20 + 200 * Math.exp(-3 * t);
  return Math.round(Math.max(20, Math.min(220, opacity)));
}

const getProps = (f: LayerFeature) => f.props as unknown as FlareProps;

/**
 * Builds a ScatterplotLayer of 311 complaint flares.
 * Opacity fades with ageSec so the map "twitches" with activity — newer
 * reports glow bright amber/orange, older ones dim gracefully.
 */
export function buildFlaresLayer(state: LayerState, _nowSec: number): ScatterplotLayer {
  return new ScatterplotLayer<LayerFeature>({
    id: LAYER_311,
    data: state.features,
    getPosition: (f) => (f.geometry as { coordinates: [number, number] }).coordinates,
    getFillColor: (f) => {
      const opacity = ageToOpacity(getProps(f).ageSec);
      return [...FLARE_BASE_COLOR, opacity];
    },
    getRadius: 40,
    radiusUnits: 'meters',
    radiusMinPixels: 2,
    radiusMaxPixels: 8,
    pickable: true,
    updateTriggers: {
      getFillColor: state.generatedAt,
    },
  });
}

export const flaresRenderer: RendererDef = {
  layerId: LAYER_311,
  build: (state, nowSec) => {
    if (!state || state.features.length === 0) return null;
    return buildFlaresLayer(state, nowSec);
  },
};
